import {
    Button, Card, Checkbox, Col, ConfigProvider, DatePicker, Divider, Empty, Modal, Row,
    Select, Space, Spin, Table, Tag, Tooltip, Typography
} from "antd";
import PropTypes from "prop-types";
import { createRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../variables/api/axiosInstance";
import {
    CheckCircleTwoTone, TeamOutlined,
    UserOutlined, BankOutlined, ReloadOutlined, EyeOutlined, FileDoneOutlined
} from "@ant-design/icons";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import { toast } from "react-toastify";
import { frenchDate } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import Footer from "../../../components/Footer";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import GeneratedBordereauCommissions from "./GeneratedBordereauCommissions";
import { StatCard } from "../../../components/statistics/StatCard";
import { COLORS } from "../../../components/ui/colors";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const TYPE_LABELS = {
    INTERMEDIAIRE: { label: "Intermédiaire", color: "purple", icon: <UserOutlined /> },
    COURTIER: { label: "Courtier", color: "geekblue", icon: <BankOutlined /> },
    AGENCE: { label: "Agence", color: "cyan", icon: <BankOutlined /> },
};

/** Aperçu imprimable d'un bordereau (une agence/intermédiaire). */
const BordereauPrintable = ({ group, periode, periodeDebut, periodeFin, draft }) => {
    const ref = createRef();
    const totalCommission = group.lignes.reduce((s, l) => s + (l.commission || 0), 0);
    const typeInfo = TYPE_LABELS[group.beneficiaireType] || TYPE_LABELS.AGENCE;

    // Logo de la compagnie (déduit des commissions du bordereau) affiché à côté de
    // l'en-tête du Pool, comme sur les autres documents de l'application.
    const [logoCompagnie, setLogoCompagnie] = useState(null);
    useEffect(() => {
        const code = (group.lignes || []).find((l) => l.organismeCode)?.organismeCode;
        if (!code) return undefined;
        let url;
        getCompagnieLogo(code)
            .then(async (response) => {
                const bytes = await response.arrayBuffer();
                const blob = new Blob([bytes], { type: "image/png" });
                url = URL.createObjectURL(blob);
                setLogoCompagnie(url);
            })
            .catch((err) => console.log(err));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [group]);

    return (
        <Card
            style={{ borderRadius: 14, marginBottom: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            bodyStyle={{ padding: 16 }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Space wrap>
                    <Tag color={typeInfo.color} style={{ borderRadius: 6 }}>{typeInfo.icon} {typeInfo.label}</Tag>
                    <Text strong style={{ color: COLORS.secondary }}>{group.beneficiaireNom || "—"}</Text>
                    <Text type="secondary">· {group.lignes.length} commission(s)</Text>
                    <Tag color="green" style={{ borderRadius: 6 }}>{displayNumbers(Math.trunc(totalCommission))} FCFA</Tag>
                    {group.numeroBordereau
                        ? <Tag color="orange" style={{ borderRadius: 6 }}>N° {group.numeroBordereau}</Tag>
                        : draft && <Tag style={{ borderRadius: 6 }}>Brouillon — n° attribué à la validation</Tag>}
                </Space>
                {/* Aucune impression dans la modale d'aperçu/génération : l'impression se fait
                    uniquement depuis les pages de bordereaux déjà validés. */}
            </div>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
                <div ref={ref}>
                    <GeneratedBordereauCommissions
                        emissions={group.lignes}
                        logoCompagnie={logoCompagnie}
                        beneficiaireNom={group.beneficiaireNom}
                        beneficiaireType={typeInfo.label}
                        numeroBordereau={group.numeroBordereau}
                        periode={periode}
                        periodeDebut={periodeDebut}
                        periodeFin={periodeFin}
                    />
                </div>
            </div>
        </Card>
    );
};

BordereauPrintable.propTypes = {
    group: PropTypes.object,
    periode: PropTypes.string,
    periodeDebut: PropTypes.string,
    periodeFin: PropTypes.string,
    draft: PropTypes.bool,
};

const CommissionsGenerees = () => {
    const navigate = useNavigate();

    const [datas, setDatas] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [dateF, setDateF] = useState([]);
    const [agenceFilter, setAgenceFilter] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [generating, setGenerating] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [generatedGroups, setGeneratedGroups] = useState([]);
    const [validated, setValidated] = useState(false); // bordereaux réellement générés ?
    // Période affichée sur les bordereaux : celle du filtre si posé, sinon PROPOSÉE d'après le
    // min/max des dates de règlement des lignes sélectionnées. L'utilisateur peut l'ajuster dans
    // l'aperçu avant génération. [dayjs|null, dayjs|null].
    const [periodeGen, setPeriodeGen] = useState([null, null]);

    // ── Chargement de toutes les commissions générables de la période ──
    const fetchData = () => {
        setLoadingData(true);
        axiosInstance
            .get(`/api/reglement/avec-commission`, {
                params: {
                    page: 0,
                    size: 5000,
                    search: "",
                    startDate: dateF && dateF[0] ? dateF[0] : null,
                    endDate: dateF && dateF.length > 1 ? dateF[1] : null,
                    compagnie: "",
                },
            })
            .then((res) => {
                setDatas(res.data.content || []);
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [dateF]);

    // ── Options du filtre par agence/intermédiaire (bénéficiaires distincts) ──
    const beneficiairesOptions = useMemo(() => {
        const map = new Map();
        datas.forEach((d) => {
            if (d.beneficiaireCode && !map.has(d.beneficiaireCode)) {
                map.set(d.beneficiaireCode, {
                    label: `${d.beneficiaireNom || d.beneficiaireCode} — ${(TYPE_LABELS[d.beneficiaireType] || {}).label || d.beneficiaireType}`,
                    value: d.beneficiaireCode,
                });
            }
        });
        return Array.from(map.values());
    }, [datas]);

    // ── Filtrage client ──
    const filteredDatas = useMemo(() => {
        return datas.filter((d) =>
            (agenceFilter.length === 0 || agenceFilter.includes(d.beneficiaireCode)) &&
            (typeFilter.length === 0 || typeFilter.includes(d.beneficiaireType))
        );
    }, [datas, agenceFilter, typeFilter]);

    // Une commission n'est SÉLECTIONNABLE (donc reversable en bordereau) que si elle porte
    // un numéro de police ET une date de règlement : sans police la ligne est incomplète,
    // sans date de règlement elle n'est pas effectivement réglée.
    const isSelectable = (d) =>
        !!(d?.numeroPolice && String(d.numeroPolice).trim()) && !!d?.dateReglement;

    // Le sous-ensemble réellement sélectionnable du jeu filtré.
    const selectableDatas = useMemo(
        () => filteredDatas.filter(isSelectable),
        [filteredDatas]
    );

    // Sélectionner tout (uniquement les lignes sélectionnables) par défaut à chaque
    // changement du jeu filtré.
    useEffect(() => {
        setSelectedRowKeys(selectableDatas.map((d) => d.codeId));
        setSelectedRows(selectableDatas);
    }, [selectableDatas]);

    // ── Indicateurs ──
    const stats = useMemo(() => {
        const sum = filteredDatas.reduce((s, d) => s + (d.commission || 0), 0);
        const nbBenef = new Set(filteredDatas.map((d) => d.beneficiaireCode)).size;
        const selSum = selectedRows.reduce((s, d) => s + (d.commission || 0), 0);
        const selBenef = new Set(selectedRows.map((d) => d.beneficiaireCode)).size;
        return { sum, nbBenef, selSum, selBenef };
    }, [filteredDatas, selectedRows]);

    const columns = [
        {
            title: "Bénéficiaire",
            dataIndex: "beneficiaireNom",
            key: "beneficiaireNom",
            render: (_, r) => {
                const t = TYPE_LABELS[r.beneficiaireType] || TYPE_LABELS.AGENCE;
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 13 }}>{r.beneficiaireNom || "—"}</Text>
                        <Tag color={t.color} style={{ borderRadius: 6, fontSize: 11 }}>{t.icon} {t.label} · {r.beneficiaireCode}</Tag>
                    </Space>
                );
            },
        },
        {
            title: "Assuré",
            dataIndex: "nom",
            key: "nom",
            render: (_, r) => `${r.nomSouscripteur || ""} ${r.prenomSouscripteur || ""}`,
        },
        { title: "Police", dataIndex: "numeroPolice", key: "numeroPolice" },
        { title: "Avenant", dataIndex: "avenant", key: "avenant", render: (_, r) => r?.avenant || "AN" },
        {
            // Prime nette = TTC (montant) - accessoires - taxes
            title: "Prime nette", dataIndex: "montant", key: "montant",
            align: "right", render: (_, r) => displayNumbers((r.montant || 0) - (r.accessoires || 0) - (r.taxes || 0)),
        },
        {
            title: "Taux", dataIndex: "tauxCommission", key: "tauxCommission",
            align: "right", render: (_, r) => `${displayNumbers(r.tauxCommission || 0)}%`,
        },
        {
            title: "Commission", dataIndex: "commission", key: "commission",
            align: "right",
            render: (_, r) => <Text strong style={{ color: COLORS.success }}>{displayNumbers(r.commission)}</Text>,
        },
        {
            title: "Date règlement", dataIndex: "dateReglement", key: "dateReglement",
            render: (_, r) => (r.modePaiement ? frenchDate(r.dateReglement) : "-"),
        },
        {
            title: "", key: "actions", width: 50,
            render: (_, r) => (
                <Tooltip title="Voir le contrat">
                    <Button size="small" shape="circle" icon={<EyeOutlined />}
                        onClick={() => navigate(`/details-contrat/${r.contratCodeId}`)} />
                </Tooltip>
            ),
        },
    ];

    // Période issue du filtre (null si aucun filtre → le bordereau la déduira des dates de règlement)
    const periodeFilter = (dateF && dateF[0])
        ? `${dayjs(dateF[0]).format("DD/MM/YYYY")} – ${dateF[1] ? dayjs(dateF[1]).format("DD/MM/YYYY") : "…"}`
        : null;
    const periodeLabel = periodeFilter || "Toutes périodes";

    // ── Génération de plusieurs bordereaux (un par bénéficiaire) ──
    const grouperSelection = (rows) => {
        const map = new Map();
        rows.forEach((r) => {
            const code = r.beneficiaireCode || "SANS_BENEFICIAIRE";
            if (!map.has(code)) {
                map.set(code, {
                    beneficiaireCode: code,
                    beneficiaireNom: r.beneficiaireNom,
                    beneficiaireType: r.beneficiaireType,
                    lignes: [],
                    numeroBordereau: null,
                });
            }
            map.get(code).lignes.push(r);
        });
        return Array.from(map.values());
    };

    // Étape 1 : aperçu (aucune écriture en base) — visualiser les bordereaux avant génération
    const previsualiser = () => {
        if (selectedRows.length === 0) {
            toast.warn("Sélectionnez au moins une commission");
            return;
        }
        setGeneratedGroups(grouperSelection(selectedRows));
        // Période à afficher : le filtre s'il est posé, sinon proposée d'après le min/max des
        // dates de règlement des lignes sélectionnées (l'utilisateur peut l'ajuster ci-dessous).
        if (dateF && dateF[0] && dateF[1]) {
            setPeriodeGen([dayjs(dateF[0]), dayjs(dateF[1])]);
        } else {
            const dates = selectedRows
                .map((r) => r.dateReglement)
                .filter(Boolean)
                .map((d) => dayjs(d))
                .filter((d) => d.isValid());
            if (dates.length) {
                let min = dates[0], max = dates[0];
                dates.forEach((d) => { if (d.isBefore(min)) min = d; if (d.isAfter(max)) max = d; });
                setPeriodeGen([min, max]);
            } else {
                setPeriodeGen([null, null]);
            }
        }
        setValidated(false);
        setResultOpen(true);
    };

    // Étape 2 : valider et générer réellement les bordereaux
    const validerEtGenerer = () => {
        setGenerating(true);
        axiosInstance
            .post(`/api/bordereau/commissions/multiple`, JSON.stringify(selectedRows), {
                params: {
                    startDate: periodeGen && periodeGen[0] ? periodeGen[0].format("YYYY-MM-DD") : null,
                    endDate: periodeGen && periodeGen[1] ? periodeGen[1].format("YYYY-MM-DD") : null,
                },
            })
            .then((res) => {
                const created = res.data || [];
                // Associer le numéro de bordereau renvoyé (suffixe = code bénéficiaire)
                setGeneratedGroups((prev) =>
                    prev.map((g) => {
                        const match = created.find((b) => (b.numeroBordereau || "").endsWith("/" + g.beneficiaireCode));
                        return { ...g, numeroBordereau: match ? match.numeroBordereau : null };
                    })
                );
                setValidated(true);
                setGenerating(false);
                toast.success(`${created.length} bordereau(x) généré(s) avec succès`);
                fetchData();
            })
            .catch((err) => {
                console.log(err);
                setGenerating(false);
                toast.error("Erreur lors de la génération des bordereaux");
            });
    };

    // Case d'en-tête PERSONNALISÉE : coche/décoche TOUTES les commissions du jeu filtré
    // (toutes les pages), pas seulement la page courante. La case native d'Ant Design ne
    // porte que sur la page affichée → le compteur « Aperçu des bordereaux » n'était pas
    // rafraîchi. Ici on met directement à jour la sélection complète.
    // « Tout » = toutes les lignes SÉLECTIONNABLES (les autres sont ignorées).
    const allSelected = selectableDatas.length > 0 && selectedRowKeys.length >= selectableDatas.length;
    const someSelected = selectedRowKeys.length > 0 && !allSelected;
    const toggleSelectAll = (checked) => {
        if (checked) {
            setSelectedRowKeys(selectableDatas.map((d) => d.codeId));
            setSelectedRows(selectableDatas);
        } else {
            setSelectedRowKeys([]);
            setSelectedRows([]);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        // Lignes sans police / sans date de règlement : case DÉSACTIVÉE (non reversables).
        getCheckboxProps: (record) => ({ disabled: !isSelectable(record) }),
        // selectedRows DÉRIVÉ des clés sur le jeu COMPLET (mais borné aux lignes sélectionnables) :
        // garantit une sélection exacte même sur plusieurs pages, et interdit toute inclusion
        // d'une ligne non sélectionnable.
        onChange: (keys) => {
            const validKeys = filteredDatas.filter((d) => keys.includes(d.codeId) && isSelectable(d));
            setSelectedRowKeys(validKeys.map((d) => d.codeId));
            setSelectedRows(validKeys);
        },
        columnTitle: (
            <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
            />
        ),
    };

    return (
        <div class="layout-wrapper layout-content-navbar">
            <div class="layout-container">
                <SideBar currentMenu={"COURTIERS"} />
                <div class="layout-page">
                    <NavBar />
                    <div class="content-wrapper">
                        <div class="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "22px 26px",
                                marginBottom: 18, color: "#fff",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}>
                                <div>
                                    <Title level={3} style={{ color: "#fff", margin: 0 }}>Commissions générées</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Générez les bordereaux de commissions par agence et intermédiaire sur une période
                                    </Text>
                                </div>
                                <Space wrap>
                                    <RangePicker
                                        format="DD/MM/YYYY"
                                        value={dateF && dateF[0] ? [dayjs(dateF[0]), dayjs(dateF[1])] : null}
                                        onChange={(v) => setDateF(v ? [v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")] : [])}
                                        placeholder={["Date début", "Date fin"]}
                                    />
                                    <Tooltip title="Rafraîchir">
                                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                                    </Tooltip>
                                </Space>
                            </div>

                            {/* Indicateurs */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Commissions" subtitle="période filtrée"
                                        value={`${filteredDatas.length}`} icon="bx bx-line-chart"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Montant commissions" subtitle="période filtrée"
                                        value={`${displayNumbers(Math.trunc(stats.sum))} F`} icon="bx bx-wallet"
                                        iconBg={COLORS.success} gradient={COLORS.success} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Bénéficiaires" subtitle="agences / intermédiaires"
                                        value={`${stats.nbBenef}`} icon="bx bx-buildings"
                                        iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Sélection" subtitle={`${stats.selBenef} bordereau(x)`}
                                        value={`${displayNumbers(Math.trunc(stats.selSum))} F`} icon="bx bx-check-square"
                                        iconBg={COLORS.warning} gradient={COLORS.warning} />
                                </Col>
                            </Row>

                            {/* Filtres + action */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={9}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Agence / Intermédiaire</Text>
                                        <Select mode="multiple" allowClear style={{ width: "100%" }}
                                            placeholder="Toutes les agences / intermédiaires"
                                            maxTagCount="responsive" optionFilterProp="label"
                                            value={agenceFilter} onChange={setAgenceFilter}
                                            suffixIcon={<TeamOutlined />}
                                            options={beneficiairesOptions} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Type de bénéficiaire</Text>
                                        <Select mode="multiple" allowClear style={{ width: "100%" }}
                                            placeholder="Tous les types"
                                            value={typeFilter} onChange={setTypeFilter}
                                            options={[
                                                { label: "Intermédiaire", value: "INTERMEDIAIRE" },
                                                { label: "Courtier", value: "COURTIER" },
                                                { label: "Agence", value: "AGENCE" },
                                            ]} />
                                    </Col>
                                    <Col xs={24} md={8} style={{ textAlign: "right", alignSelf: "flex-end" }}>
                                        <Button type="primary" size="large" icon={<EyeOutlined />}
                                            onClick={previsualiser}
                                            disabled={selectedRows.length === 0}
                                            style={{ background: COLORS.gradient, border: "none", borderRadius: 10, fontWeight: 600 }}>
                                            Aperçu des bordereaux ({stats.selBenef})
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            rowSelection={rowSelection}
                                            columns={columns}
                                            dataSource={filteredDatas}
                                            rowKey="codeId"
                                            size="middle"
                                            locale={{ emptyText: <Empty description="Aucune commission générable sur la période" /> }}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 10, showTotal: (t) => `${t} commission(s)` }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>
                        <Footer />
                        <div class="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* Modale aperçu / génération : un bordereau par agence */}
            <Modal
                title={
                    <Space>
                        <FileDoneOutlined style={{ color: COLORS.primary }} />
                        {validated
                            ? `Bordereaux générés (${generatedGroups.length})`
                            : `Aperçu des bordereaux (${generatedGroups.length})`}
                    </Space>
                }
                centered
                open={resultOpen}
                onCancel={() => setResultOpen(false)}
                width={1250}
                zIndex={2000}
                footer={
                    validated
                        ? [
                            <Button key="close" type="primary" icon={<CheckCircleTwoTone />} onClick={() => setResultOpen(false)}>
                                Terminer
                            </Button>,
                        ]
                        : [
                            <Button key="cancel" onClick={() => setResultOpen(false)}>Annuler</Button>,
                            <Button key="validate" type="primary" icon={<FileDoneOutlined />}
                                loading={generating} onClick={validerEtGenerer}
                                style={{ background: COLORS.gradient, border: "none" }}>
                                Valider et générer ({generatedGroups.length})
                            </Button>,
                        ]
                }
            >
                <div style={{
                    background: validated ? "#f6ffed" : "#fffbe6",
                    border: `1px solid ${validated ? "#b7eb8f" : "#ffe58f"}`,
                    borderRadius: 8, padding: "8px 12px",
                }}>
                    <Text>
                        {validated
                            ? <>✅ Bordereaux <b>générés et enregistrés</b>. Chaque bordereau est imprimable individuellement.</>
                            : <>👁️ <b>Aperçu</b> — aucun bordereau n'est encore enregistré. Vérifiez le contenu, puis cliquez sur <b>« Valider et générer »</b>. Pour exclure des commissions, fermez et décochez-les dans le tableau.</>}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">{selectedRows.length} commission(s)</Text>
                    </div>
                    {!validated && (
                        <div style={{ marginTop: 8 }}>
                            <Text strong>Période affichée sur les bordereaux : </Text>
                            <RangePicker
                                format="DD/MM/YYYY"
                                value={periodeGen && periodeGen[0] ? [periodeGen[0], periodeGen[1]] : null}
                                onChange={(v) => setPeriodeGen(v && v[0] ? [v[0], v[1]] : [null, null])}
                                allowClear
                                style={{ marginLeft: 4 }}
                            />
                            <div style={{ marginTop: 2 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {dateF && dateF[0]
                                        ? "Reprise du filtre appliqué — ajustable."
                                        : "Aucun filtre posé : période proposée d'après les dates de règlement. Ajustable."}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
                <Divider style={{ margin: "12px 0" }} />
                <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                    {generatedGroups.map((g) => (
                        <BordereauPrintable key={g.beneficiaireCode} group={g} periode={periodeFilter}
                            periodeDebut={periodeGen && periodeGen[0] ? periodeGen[0].format("YYYY-MM-DD") : null}
                            periodeFin={periodeGen && periodeGen[1] ? periodeGen[1].format("YYYY-MM-DD") : null}
                            draft={!validated} />
                    ))}
                </div>
            </Modal>

            <div class="layout-overlay layout-menu-toggle"></div>
            <div class="drag-target"></div>
        </div>
    );
};

export default CommissionsGenerees;
